import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HelpCircle, History, Info, Loader2, Settings2, Zap } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { hilfeOeffnen } from "@/lib/useF1Help";
import AuswahlFeld, { KUNDEN_STATUS_ERSATZ } from "@/components/AuswahlFeld";

/**
 * A-012 Status-Automatik (17.10.2026): Projekt- und Kunden-Status folgen dem Arbeitsfortschritt.
 *   • StatusAutomatikHinweis – unter dem Status-Feld: „automatisch: <Ereignis> am <Datum>“, Verlauf, Link „Regeln“
 *   • StatusAutomatikRegeln  – Einstellungen: Hauptschalter und Regel-Tabelle (Ereignis → Projekt-/Kunden-Status)
 * Logik im Backend (backend/utils/status_automatik.py): nur vorwärts, von Hand gesetzt gilt.
 */
const datum = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};
export const REGELN_LINK = "/settings?tab=status-automatik";

export const StatusAutomatikHinweis = ({ art, id, info, neuLaden = 0 }) => {
  const navigate = useNavigate();
  const [offen, setOffen] = useState(false);
  const [verlauf, setVerlauf] = useState(null);
  const tid = `status-auto-${art}`;

  useEffect(() => {
    if (!offen || !id) return undefined;
    let lebt = true;
    api.get("/status-automatik/verlauf", { params: art === "projekt" ? { projekt_id: id } : { kunde_id: id } })
      .then((r) => { if (lebt) setVerlauf(r.data || []); })
      .catch(() => { if (lebt) setVerlauf([]); });
    return () => { lebt = false; };
  }, [offen, id, art, neuLaden]);

  return (
    <div className="mt-1 text-[11px] text-muted-foreground" data-testid={tid}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {info?.ereignis_label ? (
          <span className="inline-flex items-center gap-1 text-sky-800 dark:text-sky-300" data-testid={`${tid}-hinweis`}>
            <Zap className="w-3 h-3 shrink-0" />automatisch: {info.ereignis_label} am {datum(info.at)}
          </span>
        ) : null}
        <button type="button" onClick={() => setOffen((o) => !o)} className="inline-flex items-center gap-1 underline-offset-2 hover:underline py-1" data-testid={`${tid}-verlauf-knopf`}>
          <History className="w-3 h-3" />Verlauf
        </button>
        <button type="button" onClick={() => navigate(REGELN_LINK)} className="inline-flex items-center gap-1 underline-offset-2 hover:underline py-1" data-testid={`${tid}-regeln`}>
          <Settings2 className="w-3 h-3" />Regeln
        </button>
        <button type="button" onClick={() => hilfeOeffnen("hilfe_status_automatik")} className="inline-flex items-center gap-1 underline-offset-2 hover:underline py-1" data-testid={`${tid}-hilfe`}>
          <HelpCircle className="w-3 h-3" />Hilfe
        </button>
      </div>
      {offen && (
        <div className="mt-1 rounded-sm border bg-muted/30 px-2 py-1.5 space-y-1" data-testid={`${tid}-verlauf`}>
          {verlauf === null && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {verlauf?.length === 0 && <div>Noch keine Status-Änderung durch die Automatik.</div>}
          {(verlauf || []).map((v) => (
            <div key={v.id} className={v.ergebnis === "gesetzt" ? "text-foreground" : ""} data-testid={`${tid}-zeile`}>
              <span className="tabular-nums">{datum(v.at)}</span>{" · "}
              {v.ergebnis === "gesetzt" && <><b>{v.neu}</b> ← {v.alt || "—"} ({v.ereignis_label}{v.quelle ? `: ${v.quelle}` : ""})</>}
              {v.ergebnis === "von Hand" && <>von Hand: <b>{v.neu}</b> ← {v.alt || "—"}</>}
              {v.ergebnis === "übersprungen" && <>{v.ereignis_label}: nicht geändert – {v.grund}</>}
              {v.ergebnis === "vermerkt" && <>{v.ereignis_label}{v.quelle ? `: ${v.quelle}` : ""} (ohne Statuswechsel)</>}
              {v.ergebnis === "fehler" && <span className="text-red-700">{v.ereignis_label}: Fehler – {v.grund}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const StatusAutomatikRegeln = () => {
  const [stand, setStand] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/status-automatik/regeln").then((r) => setStand(r.data)).catch(() => toast.error("Regeln konnten nicht geladen werden"));
  }, []);

  const speichern = async (neu) => {
    const vorher = stand;
    setStand(neu);
    setBusy(true);
    try {
      const r = await api.put("/status-automatik/regeln", { an: neu.an, regeln: neu.regeln });
      setStand((s) => ({ ...s, ...r.data }));
      toast.success("Gespeichert – gilt ab sofort");
    } catch (err) {
      setStand(vorher);
      toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };
  const regelSetzen = (ereignis, feld, wert) =>
    speichern({ ...stand, regeln: stand.regeln.map((r) => (r.ereignis === ereignis ? { ...r, [feld]: wert } : r)) });

  if (!stand) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const label = Object.fromEntries((stand.ereignisse || []).map((e) => [e.ereignis, e]));

  return (
    <div className="max-w-4xl space-y-4" data-testid="status-automatik-regeln">
      <div className="rounded-md border p-4 space-y-2">
        <div className="flex items-center justify-between gap-3">
          <label className="flex items-center gap-3 text-base font-semibold cursor-pointer">
            <input type="checkbox" className="w-5 h-5" checked={!!stand.an} disabled={busy}
              onChange={(e) => speichern({ ...stand, an: e.target.checked })} data-testid="status-automatik-an" />
            Status-Automatik {stand.an ? "an" : "aus"}
          </label>
          {/* A-014: Hilfe per Klick (am Handy gibt es kein F1) */}
          <button type="button" onClick={() => hilfeOeffnen("hilfe_status_automatik")}
            className="inline-flex items-center gap-1 px-2 py-1.5 text-xs border rounded-sm hover:bg-muted shrink-0" data-testid="status-automatik-hilfe">
            <HelpCircle className="w-3.5 h-3.5" />Hilfe (F1)
          </button>
        </div>
        <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            Wenn etwas passiert (linke Spalte), setzt die Suite Projekt- und Kunden-Status. Nur vorwärts: ein späterer Schritt
            setzt nie zurück, „Verloren“ nur vor dem Auftrag. Von Hand gesetzter Status gilt; die Automatik geht von dort aus weiter.
            Am Projekt/Kunden steht „automatisch: … am …“ mit Verlauf. Leeres Feld = dieser Status bleibt unverändert.
          </span>
        </div>
      </div>

      <div className="space-y-2">
        {stand.regeln.map((r, i) => (
          <div key={r.ereignis} className={`rounded-md border p-3 ${r.aktiv ? "" : "opacity-70 bg-muted/30"}`} data-testid={`regel-${r.ereignis}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="min-w-0">
                <div className="font-medium text-sm">{i + 1}. {label[r.ereignis]?.label || r.ereignis}</div>
                <div className="text-[11px] text-muted-foreground">{label[r.ereignis]?.wo}</div>
              </div>
              <label className="flex items-center gap-2 text-sm shrink-0 cursor-pointer py-1">
                <input type="checkbox" className="w-5 h-5" checked={!!r.aktiv} disabled={busy}
                  onChange={(e) => regelSetzen(r.ereignis, "aktiv", e.target.checked)} data-testid={`regel-${r.ereignis}-aktiv`} />
                aktiv
              </label>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <AuswahlFeld docType="projekt_status" label="Projekt-Status" value={r.projekt_status || ""} leer="— unverändert —"
                busy={busy} stichwoerter={false} onChange={(w) => regelSetzen(r.ereignis, "projekt_status", w)} testId={`regel-${r.ereignis}-projekt`} />
              <AuswahlFeld docType="kunden_status" label="Kunden-Status" value={r.kunden_status || ""} leer="— unverändert —"
                ersatz={KUNDEN_STATUS_ERSATZ} busy={busy} stichwoerter={false} onChange={(w) => regelSetzen(r.ereignis, "kunden_status", w)} testId={`regel-${r.ereignis}-kunde`} />
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Reihenfolge der Regeln = Reihenfolge des Arbeitsablaufs. Tragen Status eine Nummer („1.0.0 …“), entscheidet die Nummer.
        Neue Status über „+ Neu …“ in der Auswahl anlegen; sie gelten dann überall.
      </p>
    </div>
  );
};
